import { useSelector } from 'react-redux';
import { TState } from './store';
import { cloneDeep, concat, isArray, keyBy, keys } from 'lodash';
import { TAdvancedColumnData, TTab } from '../components/tabs/type';
import { useEffect, useState } from 'react';
import { TUser } from '../components/login/Login';
import { TField } from '../components/form/Form';
import { isEmpty } from '../helpers';

export const useSubTabs = () => {
  const planningTab = useSelector((state: TState) => state.planningTab);
  const duplicateTab = useSelector((state: TState) => state.duplicateTab);
  const currentTab = useSelector((state: TState) => state.currentTab);

  const [state, setState] = useState<TTab[]>([]);

  useEffect(() => {
    setState(
      (currentTab?._id === 'planning' && keys(planningTab).length
        ? concat(planningTab || [], planningTab?.childs || [])
        : currentTab?._id === 'duplicate' && keys(duplicateTab).length
          ? concat(duplicateTab || [], duplicateTab?.childs || [])
          : concat(currentTab || [], currentTab?.childs || [])) as TTab[],
    );
  }, [currentTab]);

  return state;
};

export const useAllTabs = () => {
  const planningTab = useSelector((state: TState) => state.planningTab);
  const duplicateTab = useSelector((state: TState) => state.duplicateTab);
  const tabs = useSelector((state: TState) => state.tabs);

  const [state, setState] = useState<Record<string, TTab>>({});

  useEffect(() => {
    setState(
      keyBy(
        concat(
          planningTab || [],
          duplicateTab || [],
          tabs || [],
          ...tabs.flatMap((tab) => tab.menuChilds || []),
          {
            _id: 'stats',
            title: 'Stats',
            tableColumns: [] as TAdvancedColumnData[],
            isStats: true,
          } as TTab,
          {
            _id: 'settings',
            title: 'Paramètres',
            isSetting: true,
            tableColumns: [] as TAdvancedColumnData[],
          } as TTab,
        ),
        '_id',
      ),
    );
  }, [tabs]);

  return state;
};

export const useCanI = () => {
  const isSuper = useSelector((state: TState) => state.isSuper);
  const isAdmin = useSelector((state: TState) => state.isAdmin);
  const userRole = useSelector((state: TState) => state.userRole);

  return (key: string, action: string | string[]) =>
    isSuper ||
    isAdmin ||
    (isArray(action)
      ? (action as string[]).every((k) => userRole?.[key]?.includes(k))
      : userRole?.[key]?.includes(action));
};

export const useGetSelect = () => {
  const dataInputs = useSelector((state: TState) => state.dataInputs);
  const user = useSelector((state: TState) => state.user);
  const userList = useSelector((state: TState) => state.userList);

  const [state, setState] = useState<Record<string, TField>>({});

  useEffect(() => {
    const isAdmin = user.isAdmin;
    const userRole = user.role_id;

    const usersById: Record<string, any> = keyBy(userList, '_id');

    const formatedInputs = dataInputs.map((input) => {
      if (!/user|select/.test(input.type)) return input as TField;

      const newInput = cloneDeep(input) as TField;

      if (/select/.test(input.type)) {
        newInput.allOptions = input?.formOptions;

        newInput.options = input?.formOptions?.filter(
          (opt) =>
            !opt.disabled &&
            (isAdmin ||
              !opt.roles?.length ||
              (userRole?.length &&
                user.role_id.some((id: string) => opt.roles?.includes(id)))),
        );
      }

      if (/user/.test(input.type)) {
        newInput.options =
          input.name_id === 'created_by' || !input.options
            ? userList.map((ul) => ({
                value: ul._id,
                label: ul.name,
                color: ul.userColor,
              }))
            : input.options?.map((option) => {
                const opt = usersById[option] || {};
                return {
                  value: opt._id,
                  label: opt.name,
                  color: opt.userColor,
                };
              });
      }

      return newInput;
    });

    setState(keyBy(formatedInputs, 'name_id'));
  }, [dataInputs, user, userList]);

  return (name_id: string, val?: any, isFilter?: boolean) => {
    const input = cloneDeep(state[name_id]);

    if (!input) {
      return null;
    }

    if (!isEmpty(val)) {
      if (/select/.test(input.type)) {
        input.currOption =
          /multi/.test(input.type) && !isFilter
            ? val?.map((data: any) =>
                input.allOptions?.find((fo) => fo.value === data),
              )
            : input.allOptions?.find((opt) => `${opt.value}` === `${val}`);
      }

      if (/user/.test(input.type)) {
        input.currOption = input.isMulti
          ? input.options?.filter((option: any) => val.includes(option.value))
          : input.options?.find((option: any) => option.value === val);
      }
    }

    return input;
  };
};

export const useGetUser = () => {
  const userList = useSelector((state: TState) => state.userList);

  const [state, setState] = useState<Record<string, TUser>>(
    keyBy(userList, '_id'),
  );

  useEffect(() => {
    setState(keyBy(userList, '_id'));
  }, [userList]);

  return (userId: string) => {
    const input = cloneDeep(state[userId]);

    if (!input) {
      return null;
    }

    return input;
  };
};
